// ─────────────────────────────────────────────────────────────────────────────
// WP-13 — Pantalla "Mi perfil" (/app/perfil).
//
// REQ-119: banner de campaña activa clickeable a la landing + histórico Brillamos configurable
// ON/OFF CONSISTENTE (banner y carpeta se encienden/apagan JUNTOS según la vista de la API); el CTA
// "Reconocer" solo aparece con envío activo.
// REQ-120: al ver el perfil de un COMPAÑERO solo se muestran Recibidos + Brillamos (sin enviados ni
// racha — el front ni siquiera los pide) y un botón "Reconocer a X" prellenado.
//
// SIN puntos (regla de oro). Datos REALES (feed filtrado, racha, campaña activa). RoleGuard.
//
// FASE 6 (paridad demo, H-71..H-79): reescritura completa al layout de dos columnas del demo
// (`.rp2-page` > `.rp2-sidebar` + `.rp2-main`). El namespace viejo del perfil (26 reglas) desaparece: todo
// pasa a `.rp2-*` con los valores LITERALES del demo. Fuente del demo:
// `src/features/reconocimiento/ReconocimientoPage.tsx` — OJO, en el demo el archivo
// `reconocimiento/` es el que renderiza la entrada de sidebar "Perfil" (nombres de archivo cruzados,
// ver HALLAZGOS-PARIDAD-DEMO.md §F6).
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Dispatch, SetStateAction } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { streakDayFallbackMark } from './StreakTracker';
import { rp2MinSelectableYear } from './rp2YearSelector';
import { shineLogoSrc } from './shineLogo';
import {
  useActiveCampaign,
  usePeopleSearch,
  useMyWrapped,
  useMyCampaignBadges,
  useWrappedAvailability,
  useManagerStatus,
  useUserById,
  useRecognitions,
  useNotificationPreferences,
  useSaveNotificationPreferences,
  useRecognitionRules,
  useLandingBanner,
  useUserProfile,
  useAvatarUrl,
  getBadgeName,
  getCampaignName,
  getFundamentalColor,
  getFundamentalName,
  NOTIFICATION_CATEGORY_KEYS,
  useAvatarUrlResolver,
} from '@/api/recognition';
import type { PersonSummary, NotificationCategoryKey, NotificationSelfPreferenceDto, FundamentalDto } from '@/api/recognition';
import { AnimatePresence } from 'framer-motion';
import { useUserContext } from '@/contexts/UserContext';
import { useOUItems } from '@/hooks/useOUs';
import { RoleGuard } from '@/auth/RoleGuard';
import { streakUnitCopy } from '@/utils/streakUnit';
import { RecognitionShell } from './components';
import { PostItWall } from './PostItWall';
import { WrappedModal } from './WrappedModal';
import { ProfileFolderPanel } from './ProfileFolderPanel';
import { useVistaHabilitada } from './useVistas';
import { SparkleOverlay } from './SparkleOverlay';
import { ReconocerPanel } from './ReconocerPanel';
import { getFundamentalIcon } from './fundamentalAssets';
import { PersonAvatar, isBrillamosOrigin } from './postitShared';
import { useVisibleStreak } from './useVisibleStreak';
import { fundamentalsLogoSrc } from '@/views/FundamentalsView/fundamentalsLogo';
import { useFundamentals } from '@/hooks/useFundamentals';
import { asset } from '@/utils/basePath';

type Tab = 'received' | 'sent' | 'brillamos';

/** Identificador de la carpeta Brillamos dentro de "Mis Carpetas" (las demás usan el id del Fundamental). */
const BRILLAMOS_FOLDER_ID = 'brillamos';

/**
 * RF-130 — Marca de "ya vi el aviso de que mi Recap está listo", por AÑO y por sesión de navegador
 * (paridad con el prototipo: `sessionStorage['wrappedReadySeen2026']`). Sin esto el teaser reaparecía
 * en CADA visita al perfil, porque su estado arrancaba en `true` en cada montaje.
 *
 * Por año y no global: el Recap del año siguiente es un aviso nuevo que sí merece salir otra vez.
 * En `sessionStorage` y no en `localStorage`: el prototipo lo hace así, y una sesión nueva es una
 * ocasión razonable para volver a avisar de algo que el titular quizá no llegó a abrir.
 */
const WRAPPED_TEASER_SEEN_PREFIX = 'wrappedReadySeen';

/**
 * Deep-link del aviso "tu Retrospectiva está lista" (NotificationService → `?wrapped={anio}`). Abre el
 * Wrapped en el perfil propio. El año viaja para trazar el origen, pero el gate REAL sigue siendo la
 * disponibilidad que resuelve el servidor: no se abre un Wrapped que no está disponible.
 */
const WRAPPED_PARAM = 'wrapped';

/**
 * ¿Ya se vio el aviso de este año? Un almacenamiento inaccesible (modo privado, cuota, política del
 * navegador) responde "no": el peor caso es volver a enseñar un aviso, nunca perder el Recap.
 * Envuelto en `try/catch` — regla de SonarQube del proyecto para cualquier acceso a storage.
 */
function wrappedTeaserSeen(anio: number): boolean {
  try {
    return sessionStorage.getItem(`${WRAPPED_TEASER_SEEN_PREFIX}${anio}`) !== null;
  } catch {
    return false;
  }
}

/** Marca el aviso del año como visto. Si el almacenamiento falla, el aviso volverá a salir; nada más. */
function markWrappedTeaserSeen(anio: number): void {
  try {
    sessionStorage.setItem(`${WRAPPED_TEASER_SEEN_PREFIX}${anio}`, '1');
  } catch {
    /* Sin storage no hay dedupe, y no hay nada más que hacer al respecto. */
  }
}

interface OpenFolderView {
  /** Id de la carpeta abierta; también es la `key` que reinicia la animación al cambiar de carpeta. */
  id: string;
  /** Fundamental de la carpeta; `undefined` cuando la carpeta abierta es la de Brillamos. */
  fund?: FundamentalDto;
}

/**
 * Resuelve la carpeta abierta contra el catálogo vigente. Devuelve `null` cuando no hay carpeta
 * abierta o cuando su Fundamental ya no está en el catálogo — React Query refresca el catálogo al
 * volver el foco a la ventana, así que un Fundamental desactivado con su carpeta abierta dejaría
 * `find` en `undefined`. Sin esta guarda el panel se montaría sin datos que mostrar.
 *
 * WP-E8 / RN-PR09 — `brillamosDisponible` es la misma guarda para la carpeta de Brillamos, y por
 * el mismo motivo: el toggle de administración también se refresca solo. Sin ella, apagarlo con la
 * carpeta abierta quitaba el botón pero dejaba el PANEL desplegado con el histórico a la vista —
 * exactamente el «una visible y la otra oculta» que la regla prohíbe.
 */
export function resolveOpenFolder(
  openFolderId: string | null,
  rows: ReadonlyArray<{ fund: FundamentalDto }>,
  brillamosDisponible: boolean,
): OpenFolderView | null {
  if (!openFolderId) return null;
  if (openFolderId === BRILLAMOS_FOLDER_ID) return brillamosDisponible ? { id: openFolderId } : null;
  const match = rows.find((row) => row.fund.id === openFolderId)?.fund;
  return match ? { id: openFolderId, fund: match } : null;
}

/**
 * H-75 — Racha del perfil. Sustituye la tira plana `.rec-streak` (3 números + 7 puntos) por el
 * bloque del demo: hero con llama animada, título "Esta semana" y los 7 días nombrados con sus
 * modificadores `--active` / `--today` / `--weekend`.
 */
interface Rp2WrappedButtonProps {
  /**
   * RF-130 — ¿Está DISPONIBLE el Wrapped de este titular? Sale de `GET v1/recap/availability`
   * (Gestión → campaña Brillamos de su OU → edición del Recap → fecha de ingreso), nunca de
   * `!!wrapped.data`: el handler del Wrapped devuelve siempre un DTO (con ceros si no hay agregado),
   * así que esa condición era constante `true` y el botón se mostraba a TODA LA PLANTILLA con la campaña
   * Brillamos abierta. Ésa era la causa raíz del criterio 4 del RF.
   */
  wrappedAvailable: boolean;
  onOpenWrapped: () => void;
  /** Año que CUBRE el Wrapped disponible (el de la disponibilidad), no el año en curso del reloj. */
  recapYear: number;
}

interface Rp2StreakProps extends Rp2WrappedButtonProps {
  /**
   * Task 16 — la racha del perfil es apagable por Gestión (`streakVisibleInProfile`). Llega como
   * dato y no como gate del montaje para que apagarla no se lleve por delante el Recap: son dos
   * funcionalidades distintas y el RF-130 exige que el botón exista siempre que haya disponibilidad.
   */
  showStreak: boolean;
  /**
   * BUG (racha inconsistente, 2026-08-23) — a qué programa pertenece la racha de ESTA persona ahora
   * mismo: `'Brillamos'` con campaña Brillamos activa para su OU, `undefined` (Fundamentales) si no.
   * Antes este componente llamaba `useVisibleStreak()` sin programa siempre, así que el perfil
   * mostraba la racha de Fundamentales aunque el usuario perteneciera a Brillamos — inconsistente
   * con `/app/brillamos` (`StreakWidget programa="Brillamos"`). Ver `programaDeRacha` en `PerfilView`.
   */
  programa?: string | null;
}

/**
 * Botón del Recap/Wrapped (REQ-121). Vive al pie de `.rp2-streak` por diseño, pero NO es parte de la
 * racha: se extrae para que apagarla (RF-043) no se lleve por delante el Recap, que es una
 * funcionalidad distinta y sigue disponible con la racha apagada.
 */
function Rp2WrappedButton({ wrappedAvailable, onOpenWrapped, recapYear }: Readonly<Rp2WrappedButtonProps>) {
  const { t } = useTranslation('recognition');
  // No disponible ⇒ el botón NI SE MONTA. Ocultarlo por CSS no basta: montarlo dispara las consultas
  // del Recap (regla ya establecida en el CLAUDE.md del repo).
  if (!wrappedAvailable) return null;
  return (
    <button type="button" className="rp2-wrapped-btn" onClick={onOpenWrapped}>
      <span className="rp2-wrapped-btn-sparkle" aria-hidden="true">✨</span>
      <span>{t('prof.recap', { year: recapYear })}</span>
      <span className="rp2-wrapped-btn-sparkle" aria-hidden="true">✨</span>
    </button>
  );
}

function Rp2Streak({ showStreak, wrappedAvailable, onOpenWrapped, recapYear, programa }: Readonly<Rp2StreakProps>) {
  const { t } = useTranslation('recognition');
  // RF-043 — con la racha apagada en Gestión, esta tira desaparece igual que el widget. Dos
  // interruptores independientes la apagan: el global de la funcionalidad (`useVisibleStreak`) y el
  // de la racha DEL PERFIL (`showStreak`); ninguno de los dos toca al Recap.
  const visible = useVisibleStreak(programa);
  const s = showStreak ? visible : undefined;
  const wrapped = <Rp2WrappedButton wrappedAvailable={wrappedAvailable} onOpenWrapped={onOpenWrapped} recapYear={recapYear} />;
  // Sin racha visible el panel se reduce al Recap; si tampoco lo hay, no se pinta el contenedor
  // vacío (`.rp2-streak` tiene fondo, borde y padding propios: se vería como un hueco).
  if (!s) return wrappedAvailable ? <div className="rp2-streak rp2-streak--wrapped-only">{wrapped}</div> : null;
  // RF-037 — Con periodicidad mensual el contador son MESES. El tracker de abajo sigue siendo de 7
  // días L–D: eso lo fija RF-041 y no depende del período de calificación.
  const unit = streakUnitCopy(s.periodicidad);
  const dias = t('prof.weekdayInitials').split(',');
  // índice 0 = lunes (contrato de `StreakDto.actividadSemanal`), igual que el demo.
  const todayIdx = (new Date().getDay() + 6) % 7;

  return (
    <div className="rp2-streak">
      <div className="rp2-streak-hero">
        <span className="rp2-streak-hero-flame">🔥</span>
        <div>
          <span className="rp2-streak-hero-num">{s.semanasActuales}</span>
          <span className="rp2-streak-hero-lbl">{t(unit.streakLabel)}</span>
        </div>
      </div>
      <p className="rp2-streak-title"><i className="fas fa-fire" aria-hidden="true" /> {t('prof.thisWeek')}</p>
      <div className="rp2-streak-days">
        {dias.map((d, i) => {
          const active = s.actividadSemanal[i] ?? false;
          const today = i === todayIdx;
          const weekend = i >= 5;
          // El día activo NO lleva la marca de hoy: activo gana, igual que en la tira del widget
          // (`sdc--today` también se omite cuando el día está activo). Con el día de hoy ya
          // reconocido, la única señal de "hoy" es la inicial en naranja (`rp2-day-lbl--today`)
          // — probado con un anillo encima y descartado por decisión de producto (2026-08-21).
          const cls = ['rp2-day', active ? 'rp2-day--active' : '', today && !active ? 'rp2-day--today' : '', weekend ? 'rp2-day--weekend' : '']
            .filter(Boolean).join(' ');
          // El día activo lleva el emoji del lenguaje visual de esta pantalla; el resto de los
          // glifos salen de `streakDayFallbackMark`, compartido con la tira del widget, para que
          // `·` y `✦` no signifiquen cosas distintas según la pantalla (antes HOY sin actividad
          // pintaba `·` aquí y `✦` allá, y el punto se leía como un icono que no cargó).
          const mark = active ? '⭐' : streakDayFallbackMark(today, weekend);
          return (
            <div key={`${d}-${i}`} className="rp2-day-col">
              <div className={cls}>{mark}</div>
              <span className={`rp2-day-lbl ${today ? 'rp2-day-lbl--today' : ''}`}>{d}</span>
            </div>
          );
        })}
      </div>
      {s.enRiesgo && !s.congelada && (
        <p className="rp2-streak-risk"><i className="fas fa-triangle-exclamation" aria-hidden="true" /> {t(unit.atRisk)}</p>
      )}
      {wrapped}
    </div>
  );
}

/**
 * REQ-070 — Preferencias self-service de notificaciones (solo perfil propio). Interruptor global de
 * correo + toggles por categoría (recognitionReceived / streakAndBadges / campaigns / digest, las 4
 * claves REALES que acepta el backend). Cada cambio persiste vía PUT. Estados honestos (loading/error).
 * Autoridad server-side: el userId del body lo ignora el backend (se toma del token).
 *
 * H-79 — SOLO existe en el integrado (el demo no tiene preferencias de notificación en el perfil):
 * se CONSERVA (regla 6) y se restila al lenguaje visual del demo bajo `.rp2-notifprefs*`.
 *
 * D-2 (Fase 3) — El prototipo no muestra este bloque en el Perfil. Respuesta del cliente:
 * *"Colapsarlo (acordeón cerrado)"*. Se conserva íntegro y funcional, plegado por omisión, así
 * que la pantalla se parece al maquetado de un vistazo sin apagar una preferencia real de nadie.
 * La consulta va DIFERIDA (`enabled: open`): con el bloque cerrado no se pide nada al servicio.
 */
function NotificationPreferences({ antiSpoilerActivo }: { antiSpoilerActivo: boolean }) {
  const { t } = useTranslation('recognition');
  const { demoUser } = useUserContext();
  const [open, setOpen] = useState(false);
  const prefs = useNotificationPreferences(open);
  const save = useSaveNotificationPreferences();

  const [emailEnabled, setEmailEnabled] = useState(true);
  const [cats, setCats] = useState<Partial<Record<NotificationCategoryKey, boolean>>>({});

  useEffect(() => {
    if (prefs.data) {
      setEmailEnabled(prefs.data.emailEnabled);
      setCats(prefs.data.categorias ?? {});
    }
  }, [prefs.data]);

  function persist(nextEmail: boolean, nextCats: Partial<Record<NotificationCategoryKey, boolean>>) {
    const body: NotificationSelfPreferenceDto = {
      userId: demoUser.userId || '',
      emailEnabled: nextEmail,
      categorias: nextCats,
    };
    save.mutate(body);
  }

  function toggleEmail() {
    const next = !emailEnabled;
    setEmailEnabled(next);
    persist(next, cats);
  }

  function toggleCat(key: NotificationCategoryKey) {
    // Candado anti-spoiler (RN-V03): mientras el modo esté activo, "Reconocimientos recibidos" no se
    // puede gestionar — ni encender ni apagar cambiaría nada, porque el backend ya suprime ese aviso
    // por completo mientras dure la ventana. Defensa en profundidad: el checkbox ya llega `disabled`,
    // esto cierra el hueco de un toggle disparado por teclado/programáticamente.
    if (key === 'recognitionReceived' && antiSpoilerActivo) return;
    const next = { ...cats, [key]: !(cats[key] ?? true) };
    setCats(next);
    persist(emailEnabled, next);
  }

  return (
    <section className={`rp2-notifprefs ${open ? 'rp2-notifprefs--open' : ''}`}>
      <button
        type="button"
        className="rp2-notifprefs-head"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="rp2-notifprefs-title"><i className="fas fa-bell" aria-hidden="true" /> {t('notifPrefs.title')}</span>
        <i className="fas fa-chevron-down rp2-notifprefs-caret" aria-hidden="true" />
      </button>

      {open && <p className="rp2-notifprefs-sub">{t('notifPrefs.subtitle')}</p>}

      {open && prefs.isLoading && <p className="rp2-notifprefs-state" role="status">{t('common.loading')}</p>}
      {open && prefs.isError && <p className="rp2-notifprefs-state" role="alert">{t('notifPrefs.unavailable')}</p>}

      {open && !prefs.isLoading && !prefs.isError && (
        <div className="rp2-notifprefs-list">
          <label className="rp2-notifprefs-row rp2-notifprefs-row--master">
            <span className="rp2-notifprefs-row-txt">
              <strong>{t('notifPrefs.emailEnabled')}</strong>
              <span className="rp2-notifprefs-row-hint">{t('notifPrefs.emailHint')}</span>
            </span>
            <input type="checkbox" checked={emailEnabled} onChange={toggleEmail} aria-label={t('notifPrefs.emailEnabled')} />
          </label>

          {NOTIFICATION_CATEGORY_KEYS.map((key) => {
            // RN-V03 — con el Modo Anti-Spoiler activo, "Reconocimientos recibidos" queda bloqueado:
            // el usuario no debe poder reactivar un aviso que el backend ya suprime por completo
            // mientras dure la ventana de premiación (ver RecognitionChannelSender.NotifyAsync).
            const lockedByAntiSpoiler = key === 'recognitionReceived' && antiSpoilerActivo;
            return (
              <label
                key={key}
                className={`rp2-notifprefs-row ${!emailEnabled || lockedByAntiSpoiler ? 'rp2-notifprefs-row--off' : ''}`}
              >
                <span className="rp2-notifprefs-row-txt">
                  <strong>{t(`notifPrefs.cat.${key}`)}</strong>
                  <span className="rp2-notifprefs-row-hint">
                    {lockedByAntiSpoiler ? (
                      <><i className="fas fa-lock" aria-hidden="true" /> {t('notifPrefs.catLockedAntiSpoiler')}</>
                    ) : (
                      t(`notifPrefs.catHint.${key}`)
                    )}
                  </span>
                </span>
                <input
                  type="checkbox"
                  checked={cats[key] ?? true}
                  disabled={!emailEnabled || lockedByAntiSpoiler}
                  onChange={() => toggleCat(key)}
                  aria-label={t(`notifPrefs.cat.${key}`)}
                />
              </label>
            );
          })}

          {save.isError && <p className="rp2-notifprefs-state" role="alert">{t('notifPrefs.saveError')}</p>}
        </div>
      )}
    </section>
  );
}

/**
 * Buscador de compañeros para ver su perfil (REQ-120).
 * H-79 — el demo SÍ tiene este buscador, dentro del `.rp2-sidebar` (`.rp2-search-*`): la familia
 * el namespace viejo del buscador se sustituye por el del demo en vez de solo restilarse.
 */
function ColleagueSearch({ onPick }: Readonly<{ onPick: (p: PersonSummary) => void }>) {
  // RF-008 - se resuelve id -> url UNA vez para toda la lista: `useAvatarUrl` es un hook y no se
  // puede llamar dentro del `.map()`. Comparte cache con el resto, asi que no hay peticion extra.
  const urlDeAvatar = useAvatarUrlResolver();
  const { t } = useTranslation('recognition');
  const [raw, setRaw] = useState('');
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  useEffect(() => { const id = setTimeout(() => setTerm(raw), 250); return () => clearTimeout(id); }, [raw]);
  const people = usePeopleSearch({ search: term, pageSize: 100 });
  const options = people.data?.items ?? [];
  const showDropdown = open && term.trim().length >= 2;

  return (
    <div className="rp2-search-area">
      <div className={`rp2-search-bar ${showDropdown ? 'rp2-search-bar--open' : ''}`}>
        <i className="fas fa-magnifying-glass rp2-search-icon" aria-hidden="true" />
        <input
          className="rp2-search-input"
          value={raw}
          onChange={(e) => { setRaw(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={t('prof.searchColleague')}
          aria-label={t('prof.searchColleague')}
        />
        {raw && (
          <button type="button" className="rp2-search-clear" onClick={() => { setRaw(''); setTerm(''); setOpen(false); }} aria-label={t('builder.clear')}>
            <i className="fas fa-xmark" aria-hidden="true" />
          </button>
        )}
      </div>

      {showDropdown && options.length > 0 && (
        <div className="rp2-search-dropdown">
          {options.map((p, indice) => {
            const name = p.displayName ?? p.email;
            return (
              <button key={p.id} type="button" className="rp2-search-result" onMouseDown={() => { onPick(p); setRaw(''); setTerm(''); setOpen(false); }}>
                <PersonAvatar name={name} photoUrl={p.photoUrl ?? urlDeAvatar(p.avatar)} baseClass="rp2-sr-av" colorIndex={indice} />
                <span className="rp2-sr-info">
                  <span className="rp2-sr-name">{name}</span>
                  <span className="rp2-sr-sub">{p.jobTitle ?? p.ouName ?? ''}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
      {showDropdown && !people.isLoading && options.length === 0 && (
        <div className="rp2-search-dropdown rp2-search-no-results">
          <p className="rp2-no-results-text">{t('nominate.noResults')}</p>
        </div>
      )}
    </div>
  );
}

/**
 * El botón "Ver" de Mi Equipo construye `PersonSummary` desde `TeamMemberMetricDto`, que no trae
 * jobTitle/ouName/photoUrl (llegan en null) — a diferencia de `ColleagueSearch`, cuyos resultados
 * (`usePeopleSearch`) ya vienen completos desde el directorio. La falta de AMBOS campos secundarios
 * es la señal de que `viewed` llegó así (nunca desde el buscador, que siempre los trae). Se
 * hidratan con `useUserById` (mismo patrón que `useManagerStatus`) SIN bloquear el render inicial:
 * el nombre ya está disponible de inmediato desde `location.state`, solo faltan campos secundarios
 * que pueden llegar un instante después. Extraído como hook propio (en vez de vivir inline en
 * `PerfilContent`) para no empujar su complejidad cognitiva por encima del límite del proyecto.
 */
function useHydratedViewedPerson(
  viewed: PersonSummary | null,
  setViewed: Dispatch<SetStateAction<PersonSummary | null>>,
): void {
  const needsDirectoryHydration = !!viewed && !viewed.jobTitle && !viewed.ouName;
  const directory = useUserById(needsDirectoryHydration ? viewed.id : undefined);
  useEffect(() => {
    const hydrated = directory.data;
    if (!hydrated) return;
    setViewed((current) => {
      if (!current || current.id !== hydrated.id) return current;
      if (current.jobTitle || current.ouName || current.photoUrl) return current;
      return {
        ...current,
        jobTitle: hydrated.jobTitle ?? current.jobTitle,
        ouName: hydrated.ouName ?? current.ouName,
        photoUrl: hydrated.photoUrl ?? current.photoUrl,
        // WP-E3 · RN-V08 — `ouId` también, y no es cosmético: es la CLAVE con la que se resuelve la
        // configuración del dueño de este perfil. `ouName` es la etiqueta legible del chip; sin el
        // id no se puede preguntar por su campaña activa y el perfil se gobernaría con la OU del
        // visor, que es justo lo que la regla prohíbe.
        ouId: hydrated.ouId ?? current.ouId,
      };
    });
  }, [directory.data, setViewed]);
}

/**
 * Qué carpetas de "Mis Carpetas" se montan.
 *
 * Anti-Spoiler por campaña (2026-09-23) — ya no tapa carpetas: sus conteos y su contenido salen de
 * `useRecognitions({ receptorId })`, y el servidor ya quita de ahí lo protegido.
 *
 * Fuera del componente porque `PerfilContent` ya excede el límite de complejidad.
 */
function resolverCarpetasVisibles<TFolder>(
  fundRows: readonly TFolder[],
  brillamosHistoricoVisible: boolean,
): { visibleFundFolders: readonly TFolder[]; brillamosFolderVisible: boolean; showFoldersSection: boolean } {
  const visibleFundFolders = fundRows;
  const brillamosFolderVisible = brillamosHistoricoVisible;
  return {
    visibleFundFolders,
    brillamosFolderVisible,
    showFoldersSection: visibleFundFolders.length > 0 || brillamosFolderVisible,
  };
}

function PerfilContent() {
  const { t, i18n } = useTranslation('recognition');
  const lang = i18n.language;
  const { demoUser } = useUserContext();
  const myId = demoUser.userId || undefined;

  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // El botón "Ver" de Mi Equipo navega aquí con `state: { colleague }` — mismo mecanismo de
  // "perfil de compañero" (REQ-120) que ya usa ColleagueSearch, solo con un segundo punto de
  // entrada. Se lee solo en el montaje inicial (lazy initializer): navegar DESPUÉS a otro
  // compañero vía el buscador sigue mandando por `setViewed`, no por `state`.
  const [viewed, setViewed] = useState<PersonSummary | null>(
    () => (location.state as { colleague?: PersonSummary } | null)?.colleague ?? null,
  );
  useHydratedViewedPerson(viewed, setViewed);
  const [tab, setTab] = useState<Tab>('received');
  const [showWrapped, setShowWrapped] = useState(false);
  /** Aviso cerrado en esta pantalla. La marca entre visitas la lleva `sessionStorage` (ver abajo). */
  const [teaserDismissed, setTeaserDismissed] = useState(false);
  const [openFolder, setOpenFolder] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const [showReconocerModal, setShowReconocerModal] = useState(false);
  const [showSparkle, setShowSparkle] = useState(false);

  /**
   * 🔴 WP-E3 · RN-V08 — «esa configuración gobierna siempre desde el punto de vista de quien es
   * dueño del dato mostrado (el receptor/perfil que se está consultando), NO del visor; no se
   * mezclan configuraciones de dos geografías distintas para un mismo perfil».
   *
   * Este es el ejemplo LITERAL de la regla —«ocultar el histórico de Brillamos en el perfil»— y
   * hasta aquí se resolvía al revés: la campaña salía siempre de `demoUser.ou`, la OU del VISOR,
   * incluso viendo el perfil de un compañero de otra geografía. WP-E8 lo dejó anotado con dos
   * pruebas que fijaban la frontera; este es el paquete que venía a cambiarla.
   *
   * Sale de la OU del perfil que se está mirando. `GET v1/campaigns/active?ou=` no restringe por
   * alcance, así que no hace falta endpoint nuevo — solo preguntar por la OU correcta.
   *
   * ⚠️ Si el directorio no da `ouId` (compañero que llegó por `location.state` y aún no hidratado),
   * la consulta queda deshabilitada y el histórico se comporta como antes de E3. Es el estado
   * previo, no una regresión.
   */
  const ouDelPerfil = viewed ? (viewed.ouId ?? undefined) : (demoUser.ou || undefined);
  const campaign = useActiveCampaign(ouDelPerfil);
  /**
   * BUG (racha inconsistente, reportado 2026-08-23) — `Rp2Streak` pedía SIEMPRE la racha de
   * Fundamentales (`useVisibleStreak()` sin `programa`), sin importar que `/app/brillamos` (con
   * campaña Brillamos activa) muestre la de `programa='Brillamos'`. El perfil terminaba mostrando
   * una racha distinta a la de la pantalla a la que el usuario en realidad pertenece.
   *
   * Mismo criterio EXACTO que decide a qué pantalla enruta esta persona
   * (`useRecoHomeTarget.ts`/`useActiveRecognitionEntryId` — activa = tipo Brillamos + estado
   * Activa): así el perfil siempre pide la racha del programa que le corresponde, sea cual sea.
   * Deliberadamente NO se reusa `brillamosVisible` (más abajo): esa variable además exige
   * `!brillamosApagadoPorConfig` (visibilidad del BANNER/carpeta), una pregunta distinta de "a qué
   * programa pertenece la racha de esta persona".
   */
  const campanaBrillamosActiva = campaign.data?.tipo === 'Brillamos' && campaign.data?.estado === 'Activa';
  /**
   * La campaña Brillamos de QUIEN MIRA, que es distinta de `campanaBrillamosActiva`: esa sale de
   * `ouDelPerfil` —la OU del perfil visitado— y contesta «¿a qué programa pertenece la racha de esta
   * persona?». Aquí la pregunta es «¿bajo qué campaña reconozco yo?», y el sujeto es el emisor.
   * Comparte clave de caché con la consulta de arriba cuando se mira el perfil propio, así que no
   * añade una petición.
   */
  const campanaDeQuienMira = useActiveCampaign(demoUser.ou).data;
  const brillamosDeQuienMira =
    campanaDeQuienMira?.tipo === 'Brillamos' && campanaDeQuienMira.estado === 'Activa'
      ? campanaDeQuienMira
      : null;
  const programaDeRacha = campanaBrillamosActiva ? 'Brillamos' : undefined;
  // Bug 04 — el logo del banner de campaña NO vive en `CampaignDto` (no tiene campo de imagen
  // propio); es el logo GLOBAL del programa Brillamos configurado en Gestión
  // (`GestionSettingsDto.brillamosBannerLogoUrl`, GET no-admin vía `useLandingBanner`), mismo
  // patrón/hook que ya usa `BrillamosView.tsx` para su propio hero. Fallback al asset fijo si no
  // hay config o el endpoint falla, igual que allá.
  const brillamosBanner = useLandingBanner(lang).data;
  // QA (2026-08-31) — logo: lectura EXACTA del idioma activo, NO `pickLocalized` (que cae en cascada
  // idioma → es → en → primera clave). Si el admin solo subió el logo en un idioma, la cascada lo
  // mostraba en TODOS los demás idiomas — el mismo bug de fondo ya resuelto en `BrillamosView.tsx`
  // para este mismo campo, que aquí se había quedado sin migrar. Sin logo propio del idioma activo,
  // cae al asset fijo por-idioma más abajo (`|| '/brillamos/logo-shine-white.svg'`), nunca al de otro
  // idioma.
  const brillamosBannerLogo = brillamosBanner?.brillamosBannerLogoUrl?.[lang.split('-')[0].toLowerCase()];
  // 2026-10-28 — el logo del banner de Fundamentales (Reconocimiento Diario) que el admin sube en
  // Campañas › Fundamentales ya se pintaba en el hero de `/app/reconocer` (`ReconocerView.tsx`), pero
  // el perfil seguía con el wordmark fijo «FUNDAMENTALES». Mismo campo, misma lectura EXACTA del
  // idioma activo; sin logo propio del idioma cae al arte fijo por idioma (`fundamentalsLogoSrc`).
  const fundamentalesBannerLogo = brillamosBanner?.bannerLogoUrl?.[lang.split('-')[0].toLowerCase()];
  // Catálogo de Fundamentales: `useFundamentals` (SurveyService, `/v1/catalog/fundamentals`) es la
  // ÚNICA fuente — mismo catálogo que ya consume correctamente `SafeEmptyState`/
  // `FundamentalsCatalogGrid` en el perfil de Pulso. Reemplaza a `useRecognitionCatalog`
  // (RecognitionService, `/v1/recognition-catalog`), que mantenía su propia copia desincronizada del
  // canónico (order/color/nombre desactualizados — ver PENDIENTES P2-010). Sin recognitions reales
  // en base todavía, no hay `fundamentalIds` históricos que perder al cambiar de `id`; cuando el
  // backend consolide el catálogo duplicado, este cambio ya no requiere ningún ajuste adicional.
  const catalog = useFundamentals();
  /**
   * RF-130 — Disponibilidad del Wrapped: la decisión COMPLETA la toma el servidor (interruptor de
   * Gestión → campaña Brillamos de la OU del titular → edición vigente del Recap → fecha de ingreso).
   * El cliente no vuelve a razonar sobre campañas ni fechas; sólo obedece.
   *
   * Sólo en perfil propio: el Wrapped es exclusivo del titular (criterio 5), y en el perfil de un
   * compañero no hay nada del Recap que preguntar.
   */
  const availability = useWrappedAvailability(!viewed);
  const wrappedAvailable = availability.data?.disponible === true;
  /** Año que cubre el Wrapped disponible. Rotula el botón y el aviso, y es el que se le pide a Analytics. */
  const wrappedYear = availability.data?.anio ?? 0;
  /**
   * Datos del Wrapped. Se piden con el AÑO de la disponibilidad —antes iban con `undefined`, así que
   * el año calendario del RF nunca llegaba a viajar— y SÓLO cuando hay algo que enseñar. Nada de esta
   * pantalla los pinta: el overlay los pide él mismo con las MISMAS condiciones y parámetros, así que
   * es la misma entrada de caché y esto no añade ninguna petición — sólo la adelanta para que el Recap
   * abra sin espera. Su resultado no se usa aquí a propósito: el gate del botón es la
   * DISPONIBILIDAD, nunca la presencia de datos.
   *
   * RF-130 F3 (H-07) — `desdeMes` viaja también como `fromMonth`: EXACTAMENTE el mismo valor que lee
   * `WrappedModal` de la MISMA disponibilidad (se le pasa como prop más abajo), para que esta
   * precarga y el overlay compartan la misma entrada de caché en vez de disparar dos peticiones.
   */
  useMyWrapped(
    wrappedYear || undefined,
    availability.data?.desdeMes,
    !viewed && wrappedAvailable && availability.data?.soloGenerico !== true,
  );
  // Racha (solo perfil propio) — su `record` alimenta la slide "racha más larga" del Wrapped.
  // RF-043 — con la racha apagada esta consulta no devuelve nada visible, y con ella desaparecen
  // las insignias de racha del perfil y la slide de "racha más larga" del Recap: son estadísticas de
  // racha, y el RF pide que la racha no aparezca.
  // BUG (racha inconsistente, 2026-08-23) — antes sin `programa` (siempre Fundamentales): las
  // insignias de racha (`streakBadges`, más abajo) y el récord que alimenta al Wrapped quedaban
  // desalineados con la racha que de verdad muestra `Rp2Streak` (ya corregida con este mismo
  // `programaDeRacha`) en cuanto el usuario pertenecía a Brillamos.
  const streak = useVisibleStreak(programaDeRacha, !viewed);
  // Insignias de Ganadores (Campaign) — independientes del interruptor de racha (RF-043), solo en
  // perfil propio (mismo alcance que la racha y el buscador de compañeros, REQ-120). `!viewed` y no
  // `!isColleague` porque ese último se declara más abajo en el componente.
  const campaignBadges = useMyCampaignBadges(!viewed);
  // Mi Equipo — botón visible solo si el usuario tiene reportes directos (REQ-086).
  const managerStatus = useManagerStatus(!viewed ? myId : undefined);
  // Reglas de quien mira, sin programa: su campaña activa (decisión 9). Del Anti-Spoiler solo se usa
  // `antiSpoilerActivoAhora` (ver `antiSpoilerPropio` más abajo). El buscador de compañeros ya no depende
  // de él (decisión 4): el servidor quita lo protegido de cada muro.
  const rules = useRecognitionRules();
  /**
   * Reglas por programa. Las usa la racha (`streakRules`); los dos hooks comparten caché de
   * react-query con los que ya lanza `PostItWall` para cada pestaña.
   */
  const rulesFundamentales = useRecognitionRules(undefined, 'Fundamentales');
  const rulesBrillamos = useRecognitionRules(undefined, 'Brillamos');
  // Anti-Spoiler por campaña (2026-09-23) — decisión 9: el contador «Recibidos», el candado del aviso
  // y la leyenda del muro propio siguen la campaña ACTIVA de quien mira (su Brillamos vigente o, si no
  // hay, Fundamentales), que es lo que resuelve `rules` sin programa. Las pestañas y carpetas ya no se
  // tapan: el servidor quita de cada muro lo protegido, antes de contar.
  const antiSpoilerPropio = Boolean(rules.data?.antiSpoilerActivoAhora);

  // WP-F3 / CPE-158 — las vistas de esta pantalla que la OU puede no ofrecer. El hook ya compone
  // con la otra regla de cada una (anti-spoiler, toggle de campaña, Wrapped), así que aquí NO se
  // vuelve a mirar: hacerlo repartiría la condición en dos sitios.
  const verBusqueda = useVistaHabilitada('busquedaCompaneros');
  const verHistorico = useVistaHabilitada('historicoPorAnio');
  const verResumenMensual = useVistaHabilitada('resumenMensual');
  // 2026-08-20 — recompuesto con el botón «Mi equipo» de abajo: UAT #56 (18-ago-2026) lo había
  // desconectado porque apagaba la pantalla a un líder real, pero el cliente pidió explícitamente,
  // al validar esa remediación, conservar el interruptor. Ver el comentario junto al botón.
  const verEquipo = useVistaHabilitada('verATuEquipo');
  // Task 16 — mismos toggles de Gestión, dos campos más del mismo DTO: apagan la racha (`.rp2-streak`)
  // y la lista de insignias (`.rp2-insignias`) del perfil propio, cada uno de forma independiente.
  // BUG (racha inconsistente, 2026-08-23) — `streakVisibleInProfile` leía siempre `rules`
  // (Fundamentales, sin `programa`): con Brillamos activo, Gestión podía tener la racha apagada
  // en Brillamos y encendida en Fundamentales (o viceversa) y este toggle igual respondía por la
  // configuración equivocada. Se resuelve con las reglas del MISMO programa que ya usa la racha
  // (`campanaBrillamosActiva`/`rulesBrillamos`/`rulesFundamentales`, ya declaradas arriba para el
  // anti-spoiler) — cero peticiones nuevas.
  const streakRules = campanaBrillamosActiva ? rulesBrillamos : rulesFundamentales;
  const streakVisibleInProfile = streakRules.data?.streakVisibleInProfile ?? true;
  const badgesVisible = rules.data?.badgesEnabled ?? true;

  const isColleague = viewed !== null;
  const targetId = isColleague ? viewed.id : myId;

  // Deep-link ?wrapped={anio} (aviso "tu Retrospectiva está lista"): abre el Wrapped en cuanto el
  // servidor confirma que hay algo que enseñar. Se VALIDA la disponibilidad (regla del usuario: siempre
  // validar antes de abrir un destino condicionado): si no está disponible —otro año, apagado, o perfil
  // de un compañero— no se fuerza el overlay. El parámetro se limpia (replace) tras leerlo para no
  // reabrir el Wrapped al navegar hacia atrás ni dejarlo pegado en la URL.
  useEffect(() => {
    if (!searchParams.has(WRAPPED_PARAM)) return;
    if (availability.isLoading) return; // esperar el veredicto del servidor antes de decidir
    if (!isColleague && wrappedAvailable && verResumenMensual) setShowWrapped(true);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(WRAPPED_PARAM);
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, availability.isLoading, wrappedAvailable, verResumenMensual, isColleague]);

  // H-74/H-76 — contadores y barras por Fundamental del `.rp2-sidebar`. Un lote del feed real
  // basta para el desglose; `totalCount` da el número exacto de recibidos/enviados.
  const received = useRecognitions({ receptorId: targetId, page: 1, pageSize: 100 });

  // H-76 — reparto de recibidos por Fundamental (datos reales del feed).
  const recibidos = received.data?.items ?? [];
  /**
   * Base de "Mis Carpetas" (conteos, barras de progreso y contenido de la carpeta de un
   * Fundamental): excluye origen Brillamos, mismo criterio que ya usa el muro "Recibidos"
   * (`programa: 'Fundamentales'`). Sin este filtro, una carpeta de Fundamental podía mostrar un
   * post-it de Brillamos que el muro de arriba ya no exhibe — y ese mismo post-it aparecía
   * DUPLICADO en la carpeta de Brillamos — bug reportado 2026-08-07 (ronda de revisión).
   */
  const recibidosFundamentales = recibidos.filter((item) => !isBrillamosOrigin(item));
  /**
   * Carpeta "Brillamos" de "Mis Carpetas": contenido a listar al abrirla. Sale del mismo lote de
   * `recibidos` (page 1, 100 más recientes) — límite compartido con las carpetas de Fundamentales,
   * no es un problema nuevo de esta corrección.
   */
  const brillamosItems = recibidos.filter((item) => isBrillamosOrigin(item));
  /**
   * TOTAL real de recibidos con origen Brillamos — pestaña "⭐ Brillamos Juntos" y el badge de la
   * carpeta usan este valor, NO `brillamosItems.length` (topado a los 100 recibidos más recientes
   * de `received`). Consulta liviana con `pageSize: 1` (mismo patrón que `sent` más arriba, que
   * también solo necesita `totalCount`), sin `campaignId` (histórico completo). Sin esto, un
   * usuario con más de 100 reconocimientos recibidos cuyo histórico de Brillamos cayera fuera de
   * esa ventana de 100 no vería la pestaña aunque sí tuviera contenido real — bug reportado
   * 2026-08-07 (ronda de revisión).
   */
  const brillamosTotal = useRecognitions({ receptorId: targetId, programa: 'Brillamos', page: 1, pageSize: 1 });
  const brillamosCount = brillamosTotal.data?.totalCount ?? 0;
  /**
   * 2026-08-24 (corregido tras QA) — los contadores "Recibidos"/"Enviados" de `.rp2-stats` (arriba
   * del nombre) deben ser el MISMO número que se ve "adentro" de la campaña activa del perfil, no un
   * combinado: con campaña Brillamos activa (`campanaBrillamosActiva`, misma bandera que ya usa
   * `programaDeRacha`), muestran SOLO Brillamos — y con `soloVigentes: true`, que acota a cualquier
   * campaña Brillamos vigente AHORA en cualquier OU del tenant (no solo la propia OU del que mira):
   * dos personas con campañas Brillamos DISTINTAS pero ambas activas siguen contando entre sí. Sin
   * campaña Brillamos activa (día a día), muestran TODO lo de Fundamentales — nunca mezclado con
   * Brillamos histórico. Consulta aparte de `brillamosTotal` (arriba, histórico sin acotar —
   * alimenta la pestaña ⭐ y la carpeta, que no cambian aquí).
   */
  const receivedBrillamosVigenteTotal = useRecognitions({
    receptorId: targetId, programa: 'Brillamos', soloVigentes: true, page: 1, pageSize: 1,
  });
  const sentBrillamosVigenteTotal = useRecognitions({
    emisorId: myId, programa: 'Brillamos', soloVigentes: true, page: 1, pageSize: 1,
  });
  const receivedFundamentalesTotal = useRecognitions({
    receptorId: targetId, programa: 'Fundamentales', page: 1, pageSize: 1,
  });
  const sentFundamentalesTotal = useRecognitions({
    emisorId: myId, programa: 'Fundamentales', page: 1, pageSize: 1,
  });
  const receivedStatTotal = campanaBrillamosActiva
    ? (receivedBrillamosVigenteTotal.data?.totalCount ?? 0)
    : (receivedFundamentalesTotal.data?.totalCount ?? 0);
  const sentStatTotal = campanaBrillamosActiva
    ? (sentBrillamosVigenteTotal.data?.totalCount ?? 0)
    : (sentFundamentalesTotal.data?.totalCount ?? 0);

  /**
   * WP-E8 · RN-PR09 — «El toggle debe controlar de forma consistente ambas partes (banner +
   * histórico); no puede quedar una visible y la otra oculta». Hasta aquí NO era así: el banner
   * miraba el toggle y el histórico (pestaña ⭐ y carpeta) solo el conteo, así que apagarlo desde
   * administración escondía el banner y dejaba la carpeta a la vista.
   *
   * 🔴 `campaign.data` tiene TRES estados y solo uno es un apagado (`useActiveCampaign`,
   * `hooks.ts:207`): `undefined` = cargando, `null` = 204, no hay campaña activa, y el objeto con
   * `visibilidad.recibidos === false` = apagado explícito. Colgar el histórico de `brillamosVisible`
   * a secas lo habría borrado también cuando NO hay campaña —el estado normal casi todo el año—, y
   * el histórico es del usuario, no de la campaña: comportamiento fijado el 2026-08-07
   * (`perfilBrillamosTab.test.tsx`, `perfilBrillamosFolder.test.tsx`) y que esta unificación
   * conserva a propósito.
   */
  const brillamosApagadoPorConfig = campaign.data ? !campaign.data.visibilidad.recibidos : false;
  // REQ-119 — banner de campaña activa + insignia del avatar: además del toggle necesitan una
  // campaña vigente que anunciar (nombre, id), por eso siguen exigiendo `campaign.data`.
  const brillamosVisible = Boolean(campaign.data) && !brillamosApagadoPorConfig;
  /**
   * ✅ WP-E3 · RN-V08 — RESUELTO. WP-E8 dejó aquí un `!isColleague` porque la campaña salía de la OU
   * del VISOR y aplicársela al perfil de un compañero habría sido el defecto exacto que la regla
   * prohíbe. Ya no: `campaign` se pide con la OU del PERFIL (ver `ouDelPerfil`, arriba), así que el
   * toggle que gobierna es siempre el de su dueño — en el perfil propio y en el ajeno.
   *
   * Sin quitar ese guardia, E3 habría cambiado de dónde sale la campaña y el perfil ajeno habría
   * seguido sin aplicarla: media reforma, que es peor que ninguna porque parece hecha.
   */
  const brillamosApagadoParaEstePerfil = brillamosApagadoPorConfig;
  /**
   * Histórico de Brillamos — pestaña "⭐ Brillamos Juntos" y carpeta de "Mis Carpetas". Mismo
   * toggle que el banner (RN-PR09) y además RN-PR08: una sección sin datos se oculta por completo
   * en vez de mostrarse en cero.
   *
   * Anti-Spoiler por campaña (2026-09-23) — excepción en el perfil PROPIO: si su campaña Brillamos
   * tiene el modo activo, la pestaña se queda aunque el servidor la devuelva vacía (lo ha filtrado),
   * para que `PostItWall` explique por qué. Sin esto la pestaña desaparecía sin decir nada, que es
   * tapar la pestaña (decisión 4: se ocultan solo las tarjetas). Lo destapó la prueba en navegador.
   */
  const antiSpoilerBrillamosPropio = !isColleague && Boolean(rulesBrillamos.data?.antiSpoilerActivoAhora);
  const brillamosHistoricoVisible = !brillamosApagadoParaEstePerfil
    && (brillamosCount > 0 || antiSpoilerBrillamosPropio);

  // Si al ver un compañero el tab activo era "enviados", forzamos "recibidos" (el par no ve enviados).
  useEffect(() => { if (isColleague && tab === 'sent') setTab('received'); }, [isColleague, tab]);
  // Si se apaga Brillamos y estábamos en esa pestaña, volvemos a recibidos. Se mira la condición
  // unificada (RN-PR09): apagar el toggle desde administración también tiene que sacarnos de ahí,
  // no solo quedarse el histórico a cero.
  useEffect(() => { if (!brillamosHistoricoVisible && tab === 'brillamos') setTab('received'); }, [brillamosHistoricoVisible, tab]);
  // Al cambiar de perfil (propio ↔ compañero, o de un compañero a otro) cerramos la carpeta abierta:
  // sus items pertenecen al `targetId` anterior y el panel quedaría mostrando datos de otra persona.
  useEffect(() => { setOpenFolder(null); }, [targetId]);
  // Anti-Spoiler por campaña (2026-09-23) — las pestañas de recibidos ya no desaparecen: el servidor
  // quita de su muro lo protegido (decisión 4, se ocultan solo las tarjetas).
  const availableTabs = useMemo<Tab[]>(() => {
    const tabs: Tab[] = ['received'];
    if (!isColleague) tabs.push('sent');
    if (brillamosHistoricoVisible) tabs.push('brillamos');
    return tabs;
  }, [isColleague, brillamosHistoricoVisible]);

  const profileName = isColleague ? (viewed.displayName ?? viewed.email) : (demoUser.nombre || demoUser.correo);
  const profilePuesto = isColleague ? (viewed.jobTitle ?? '') : demoUser.puesto;
  const profileArea = isColleague ? '' : demoUser.departamento;
  // El chip muestra el NOMBRE de la OU; `demoUser.ou`/`viewed.ouId` es la clave que viaja a las
  // APIs. Ya se corrigió en backend (fix(resolve) 2026-08-28) que `ouName`/`ouNombre` llegue
  // siempre resuelto desde `/v1/users/resolve`, así que el catálogo completo de OUs solo se pide
  // como RESPALDO — cuando de verdad falta el nombre (OU borrada del catálogo, por ejemplo) — y no
  // en cada visita a "Mi perfil": es de las pantallas más vistas de la app y pedir ahí el catálogo
  // entero del tenant en cada render sería costo innecesario a la escala global de PMP.
  const necesitaCatalogoOu = isColleague ? !viewed.ouName : !demoUser.ouNombre;
  const { data: ouCatalogItems = [] } = useOUItems({
    activeOnly: false,
    rootsOnly: false,
    enabled: necesitaCatalogoOu,
  });
  const ouNameById = useMemo(
    () => new Map(ouCatalogItems.map(ouItem => [ouItem.id, ouItem.name])),
    [ouCatalogItems],
  );
  const profileOu = isColleague
    ? (viewed.ouName || (ouDelPerfil ? ouNameById.get(ouDelPerfil) : undefined) || '')
    : (demoUser.ouNombre || ouNameById.get(demoUser.ou) || demoUser.ou);
  // Foto de AD/Entra ID — PENDIENTE de backend (ver PENDIENTES.md); hoy siempre undefined/null,
  // así que PersonAvatar cae al avatar de iniciales de siempre sin cambio visual.
  const photoUrlDeGraph = isColleague ? viewed.photoUrl : demoUser.fotoUrl;

  /**
   * RF-008 — el avatar que la persona eligió al darse de alta. Sale de
   * `GET v1/users/{id}/profile`, que es el único endpoint que lo devuelve (`UserDto` está congelado).
   *
   * Sólo se pide para el perfil PROPIO: el avatar de un colega llegaría por el feed, y de dónde saca
   * el feed su foto es otro paquete — pedir aquí el perfil de cada colega que se visita sería añadir
   * una petición por visita para un campo que su DTO puede acabar trayendo solo.
   *
   * **Precedencia (D4.b): si hay foto de Graph, GANA la foto de Graph.** Hoy `photoUrl` es siempre
   * `undefined` para todo el mundo, así que esta regla no se puede observar en QA todavía: es la
   * conducta correcta escrita por adelantado, con su prueba simulando `photoUrl`.
   */
  const miPerfil = useUserProfile(isColleague ? null : myId);
  // 🔴 El de un COLEGA ya no se queda en null: llega en el propio `viewed` (`UserDto.Avatar`, RF-008),
  // así que no hace falta pedir su perfil — una petición por colega visitado para un solo campo.
  const avatarElegido = isColleague ? viewed.avatar : miPerfil.data?.avatar;
  const urlDelAvatarElegido = useAvatarUrl(photoUrlDeGraph ? null : avatarElegido);
  const profilePhotoUrl = photoUrlDeGraph ?? urlDelAvatarElegido;

  // Orden/color/ícono/nombre/id de "Mis Carpetas" y de las barras por Fundamental: todo sale del
  // catálogo de SurveyService (`catalog`, fuente correcta). `dailyHabits`/`attitudes` quedan vacíos
  // a propósito — el catálogo de SurveyService trae los suyos propios (otro dominio, hábitos de
  // Pulso) y esta pantalla no los usa; ni `ProfileFolderPanel` ni `WrappedModal` leen esos campos.
  const funds: FundamentalDto[] = [...(catalog.data ?? [])]
    .map((item): FundamentalDto => ({
      id: item.id,
      code: item.code,
      order: item.order,
      names: item.names,
      shortName: item.shortName,
      shortNames: item.shortNames,
      color: item.color,
      icon: item.icon,
      image: item.image,
      isActive: item.isActive,
      dailyHabits: [],
      attitudes: [],
    }))
    .sort((first, second) => (first.order ?? 0) - (second.order ?? 0) || first.code.localeCompare(second.code));
  /** Todos los Fundamentales del catálogo con su conteo — incluidos los que están a cero. */
  const fundCounts = funds.map((fund, index) => ({
    fund,
    index,
    count: recibidosFundamentales.filter((item) => item.fundamentalIds.includes(fund.id)).length,
  }));
  /**
   * Las BARRAS de progreso y las CARPETAS solo muestran los Fundamentales con reconocimientos: una
   * barra o carpeta vacía no aporta información. Si ningún Fundamental tiene reconocimientos y
   * Brillamos tampoco (o no está visible), la sección "Mis Carpetas" completa se oculta.
   */
  const fundRows = fundCounts.filter((row) => row.count > 0);
  // BUG-ANTISPOILER — "Mis Carpetas" es RECIBIDOS (Fundamentales + Brillamos), nunca Enviados: con el
  // modo activo, las carpetas afectadas y su panel expandido NO SE MONTAN — nada de blur para esta
  // pantalla (decisión de producto), mismo criterio que `ColleagueSearch` más arriba.
  //
  // 🔴 Bug fila 61 — el gate es POR PROGRAMA y por carpeta, no uno global para toda la sección: esta
  // era la fuga confirmada en pantalla el 2026-08-20. Con el anti-spoiler de Brillamos encendido, su
  // tarjeta salía difuminada arriba pero su carpeta seguía montada aquí y enseñaba emisor, mensaje y
  // fecha EN CLARO — la segunda vía sin bloquear hacia lo mismo que la pestaña tapa.
  const {
    visibleFundFolders, brillamosFolderVisible, showFoldersSection,
  } = resolverCarpetasVisibles(
    // La carpeta sí exige datos: una carpeta vacía no explica nada (la leyenda vive en la pestaña).
    fundRows, brillamosHistoricoVisible && brillamosCount > 0);
  // Bug fila 61 — se resuelve contra `brillamosFolderVisible` (que ya descuenta el anti-spoiler de
  // Brillamos) y no contra `brillamosHistoricoVisible`: el panel expandido es la vía por la que se
  // veía el contenido en claro, así que el gate tiene que estar TAMBIÉN aquí y no solo en el tile —
  // un `openFolder` que sobreviva (estado previo, toggle en caliente) no debe poder abrirlo.
  const openFolderView = resolveOpenFolder(openFolder, fundCounts, brillamosFolderVisible);
  /** Fundamental de la carpeta abierta; `undefined` si la carpeta abierta es la de Brillamos. */
  const openFund = openFolderView?.fund;

  // H-77 — insignias de racha, gobernadas por `badgesEnabled` (Task 16, Gestión → solo racha).
  const streakBadges = badgesVisible ? (streak?.insignias ?? []) : [];
  // Insignias de Ganadores (Campaign): NO pasan por `badgesVisible` — ese toggle es de racha, y una
  // medalla de Ganadores ya otorgada no puede depender de un interruptor ajeno a esa funcionalidad.
  const badges = [...streakBadges, ...campaignBadges];
  const currentYear = new Date().getFullYear();
  // H-77 — el selector de año (`.rp2-year`) permitía retroceder sin tope. Ver `rp2YearSelector.ts`.
  const minSelectableYear = rp2MinSelectableYear(currentYear);

  /**
   * RF-130 — Aviso "tu Recap está listo". Se monta SÓLO con disponibilidad confirmada, en perfil
   * propio, con el overlay cerrado y mientras no se haya visto ya el aviso de ESE año. La marca de
   * "visto" se lee cuando la disponibilidad resuelve (antes no se sabe de qué año hablar).
   */
  const teaserAlreadySeen = useMemo(
    () => wrappedYear > 0 && wrappedTeaserSeen(wrappedYear),
    [wrappedYear],
  );
  // 2026-11-03 — con la vista «Resumen» apagada en Vistas, ni botón ni aviso: el visor ya exigía
  // `verResumenMensual` y antes el botón aparecía pero no abría nada.
  const wrappedVisible = wrappedAvailable && verResumenMensual;
  const showWrappedTeaser = wrappedVisible && !isColleague && !showWrapped
    && !teaserDismissed && !teaserAlreadySeen;
  /** Cerrar el aviso lo cierra PARA LA SESIÓN, no sólo para este render. */
  const dismissWrappedTeaser = () => {
    setTeaserDismissed(true);
    if (wrappedYear > 0) markWrappedTeaserSeen(wrappedYear);
  };

  const emptyNode = tab === 'received' || tab === 'sent'
    ? (
      <div className="rp2-sigma-card">
        <div className="rp2-sigma-card-inner">
          <img
            src={fundamentalesBannerLogo || fundamentalsLogoSrc(lang)}
            alt=""
            className={fundamentalesBannerLogo ? 'rp2-sigma-logo rp2-sigma-logo--custom' : 'rp2-sigma-logo'}
          />
          <p className="rp2-sigma-msg">{t('prof.sigmaMsg')}</p>
          <span className="rp2-sigma-from">{t('prof.sigmaFrom', { year: selectedYear })}</span>
        </div>
        <p className="rp2-sigma-note">
          {t(tab === 'sent' ? 'prof.sigmaNoteSent' : 'prof.sigmaNote', { year: selectedYear })}
        </p>
      </div>
    )
    : <p className="rp2-empty">{t('profile.empty')}</p>;

  return (
    <div className="rp2-page">
      {/* ── IZQUIERDA: tarjeta de perfil ── */}
      <aside className="rp2-sidebar">
        {/* 2026-08-20 — vuelve a componerse con `verEquipo` (`useVistaHabilitada('verATuEquipo')`,
            WP-F3/CPE-158): UAT #56 (18-ago-2026) lo había quitado porque apagaba la pantalla a un
            líder real en cuanto su OU tenía la vista apagada. Al validar esa remediación, el
            cliente pidió explícitamente conservar el interruptor, así que vuelve a componerse aquí
            — misma tarjeta en Admin › Vistas, mismo gate en el botón y en `/app/equipo`
            (`TeamView.tsx`). */}
        {!isColleague && managerStatus.data?.isManager && verEquipo && (
          <Link to="/app/equipo" className="rp2-team-btn">
            <i className="fas fa-users" aria-hidden="true" /> {t('prof.myTeam')}
          </Link>
        )}

        {!isColleague && rules.isSuccess && verBusqueda && (
          <ColleagueSearch onPick={(p) => { setViewed(p); setTab('received'); }} />
        )}

        {isColleague && (
          <button type="button" className="rp2-back-btn" onClick={() => setViewed(null)}>
            <i className="fas fa-chevron-left" aria-hidden="true" /> {t('prof.backToMine')}
          </button>
        )}

        {/* BUG (Julio, 2026-08-18) — este banner mostraba SIEMPRE el logo de Fundamentales, sin
            importar qué programa tuviera la campaña activa del perfil. Ahora sigue al `tipo` de
            `campaign.data` (única señal real disponible: el contrato de campaña no trae ninguna
            imagen propia por campaña, solo `tipo`) — Brillamos activa muestra su propio logo;
            Fundamentales, Personalizada o sin campaña activa conservan el logo de Fundamentales
            (comportamiento original). El logo de Fundamentales sigue dependiendo del idioma
            (mismo helper que `FundamentalsView`, la pantalla de Fundamentales de Pulso): no hay
            arte propio para de/nl y cae a inglés, respaldo ya acordado.
            QA (2026-08-31) — DT-029: la rama Brillamos usaba la ruta fija `/brillamos/logo-shine-
            color.svg` (el wordmark genérico, de facto en inglés) en vez de `shineLogoSrc(lang)`, así
            que nunca cambiaba de idioma — a diferencia de la rama de Fundamentales, que sí lo hacía.
            Homologado al mismo helper que ya usa `BrillamosView.tsx`.
            2026-10-28 — fuera de Brillamos, si el admin subió el logo del banner de Fundamentales
            (Reconocimiento Diario), se pinta ese en lugar del wordmark fijo, igual que en `/app/reconocer`. */}
        <div className="rp2-banner">
          {campaign.data?.tipo === 'Brillamos' ? (
            <img src={shineLogoSrc(lang, 'color')} alt="" className="rp2-banner-logo" />
          ) : (
            <img
              src={fundamentalesBannerLogo || fundamentalsLogoSrc(lang)}
              alt=""
              className={fundamentalesBannerLogo ? 'rp2-banner-logo rp2-banner-logo--custom' : 'rp2-banner-logo'}
            />
          )}
        </div>

        <div className="rp2-avatar-wrap">
          <PersonAvatar name={profileName} photoUrl={profilePhotoUrl} baseClass="rp2-avatar" />
          {brillamosVisible && !isColleague && (
            <div className="rp2-shine-badge" title={t('prof.brillamosFolder')}>
              {/* QA (2026-08-31) — DT-029: mismo defecto que el banner de arriba, ruta fija en vez de
                  `shineLogoSrc(lang)`. */}
              <img src={shineLogoSrc(lang, 'color')} alt="" className="rp2-shine-badge-logo" />
            </div>
          )}
        </div>

        {/* H-73 — identidad */}
        <div className="rp2-info">
          <h2 className="rp2-name">{profileName}</h2>
          {profilePuesto && <p className="rp2-puesto">{profilePuesto}</p>}
          <div className="rp2-meta-row">
            {profileArea && <span className="rp2-meta-chip"><i className="fas fa-briefcase" aria-hidden="true" /> {profileArea}</span>}
            {profileOu && <span className="rp2-meta-chip"><i className="fas fa-location-dot" aria-hidden="true" /> {profileOu}</span>}
          </div>
        </div>

        {/* H-74 — contadores. Decisión 9 — "Recibidos" del perfil PROPIO se oculta si su campaña
            activa tiene el Anti-Spoiler (`antiSpoilerPropio`); en el de un compañero el número ya
            sale sin lo protegido (el servidor filtra antes de contar). "Enviados" nunca se oculta, uno mismo no es
            spoiler de lo que uno mismo envió. `receivedStatTotal`/`sentStatTotal` (2026-08-24) son
            el MISMO número que dentro de la campaña activa: con Brillamos activa, solo Brillamos
            vigente (cualquier OU); sin ella, todo Fundamentales — ver comentario junto a su
            definición. */}
        <div className="rp2-stats">
          {!(antiSpoilerPropio && !isColleague) && (
            <div className="rp2-stat">
              <span className="rp2-stat-num">{receivedStatTotal}</span>
              <span className="rp2-stat-lbl">{t('profile.received')}</span>
            </div>
          )}
          {!isColleague && (
            <>
              {!antiSpoilerPropio && <div className="rp2-stat-sep" />}
              <div className="rp2-stat">
                <span className="rp2-stat-num">{sentStatTotal}</span>
                <span className="rp2-stat-lbl">{t('profile.sent')}</span>
              </div>
            </>
          )}
        </div>

        {/* H-75 — racha (solo perfil propio, REQ-120). Task 16 — apagable desde Gestión
            (`streakVisibleInProfile`), que viaja como PROP y no como gate del montaje: apagar la
            racha no puede llevarse por delante el botón del Recap (RF-130 × RF-043). */}
        {!isColleague && (
          <Rp2Streak
            showStreak={streakVisibleInProfile}
            programa={programaDeRacha}
            wrappedAvailable={wrappedVisible}
            // H-11 — abrir el Recap por el BOTÓN también marca el aviso como visto: si no, al cerrar
            // el overlay `showWrapped` vuelve a `false` y el aviso reaparece justo después de haberlo
            // visto entero. Reusa `dismissWrappedTeaser` (mismo `sessionStorage` que usa el aviso) en
            // vez de escribir la marca por su cuenta.
            onOpenWrapped={() => { dismissWrappedTeaser(); setShowWrapped(true); }}
            recapYear={wrappedYear}
          />
        )}

        {/* H-76 — barras por Fundamental */}
        {fundRows.length > 0 && (
          <div className="rp2-funds">
            <p className="rp2-funds-title">{t('catalog.title')}</p>
            {fundRows.map(({ fund, index, count }) => (
              <div key={fund.id} className="rp2-fund-row">
                <img src={getFundamentalIcon(fund, index)} alt="" className="rp2-fund-icon" />
                <div className="rp2-fund-track">
                  <div
                    className="rp2-fund-fill"
                    style={{ width: `${(count / recibidosFundamentales.length) * 100}%`, background: getFundamentalColor(fund, index) }}
                  />
                </div>
                <span className="rp2-fund-cnt">{count}</span>
              </div>
            ))}
          </div>
        )}

        {/* H-78 — CTA "Reconocer a X" (perfil de compañero). Se ofrece SIEMPRE: con una campaña
            Brillamos en curso no desaparece, el envío se atribuye a esa campaña (ver el modal). */}
        {isColleague && (
          <div className="rp2-reconocer-wrap">
            <button type="button" className="rp2-reconocer-btn" onClick={() => setShowReconocerModal(true)}>
              <i className="fas fa-star" aria-hidden="true" /> {t('prof.recognizeName', { name: profileName })}
            </button>
          </div>
        )}
        {/* H-77 — insignias (racha + Ganadores). `badgesVisible` (Gestión) ya se aplicó al armar
            `badges`: solo gobierna las de racha, nunca las de Ganadores (Campaign). */}
        {!isColleague && badges.length > 0 && (
          <div className="rp2-insignias">
            <p className="rp2-insignias-label">{t('prof.badges')}</p>
            <div className="rp2-insignias-list">
              {badges.map((b) => (
                <div key={b.id} className="rp2-insignia-item">
                  <img src={b.iconUrl || asset('/color-icon.png')} alt="" className="rp2-insignia-img" />
                  <div className="rp2-insignia-meta">
                    <span className="rp2-insignia-name">{getBadgeName(b, lang)}</span>
                    {b.unlockedAt && <span className="rp2-insignia-year">{new Date(b.unlockedAt).getFullYear()}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </aside>

      {/* ── DERECHA: muro de reconocimientos ── */}
      <div className="rp2-main">
        {/* H-78 / REQ-119 — banner de campaña activa */}
        {/* 🔴 WP-E3 — el banner queda acotado al perfil PROPIO. Desde que la campaña sale de la OU
            del perfil consultado, en el de un compañero anunciaría la campaña de OTRA geografía
            enlazando a MI landing (`/app/brillamos`): dos cosas que no casan. Y no es una desviación
            suelta — la insignia del avatar, que es su par visual, ya estaba acotada así. */}
        {brillamosVisible && !isColleague && campaign.data && (
          <Link to="/app/brillamos" className="rp2-brillamos-banner">
            {/* QA (2026-09-01) — el fallback (sin override del admin) se había quedado en la ruta fija
                genérica en vez de `shineLogoSrc(lang)`: se arregló la lectura EXACTA del override
                arriba (`brillamosBannerLogo`) pero no este fallback, mismo patrón que
                `BrillamosView.tsx:311` (`brillamosLogo || shineLogoSrc(lang)`). */}
            <img
              src={brillamosBannerLogo || shineLogoSrc(lang, 'white')}
              alt=""
              className="rp2-bann-logo"
            />
            <span className="rp2-bann-text">
              <span className="rp2-bann-title">{getCampaignName(campaign.data, lang)}</span>
              <span className="rp2-bann-sub">{t('prof.campaignBannerSub')}</span>
            </span>
          </Link>
        )}

        {isColleague && (
          <div className="rp2-viewing-badge">
            <i className="fas fa-circle-user" aria-hidden="true" /> {t('prof.viewingTitle', { name: profileName })}
          </div>
        )}

        {/* H-78 — pestañas */}
        <div className="rp2-tabs" role="tablist">
          {availableTabs.map((tk) => (
            <button key={tk} type="button" role="tab" aria-selected={tab === tk}
              className={`rp2-tab ${tab === tk ? 'rp2-tab--active' : ''}`} onClick={() => setTab(tk)}>
              {tk === 'received' && t('profile.received')}
              {tk === 'sent' && t('profile.sent')}
              {tk === 'brillamos' && <><span aria-hidden="true">⭐</span> {t('prof.brillamosFolder')}</>}
              {tab === tk && <span className="rp2-tab-bar" />}
            </button>
          ))}
        </div>

        {/* H-77 — selector de año, acotado a [currentYear-2, currentYear] */}
        <div className="rp2-year-row">
          <button type="button" className="rp2-year-arrow-btn" disabled={selectedYear <= minSelectableYear}
            onClick={() => setSelectedYear((y) => Math.max(minSelectableYear, y - 1))} aria-label={String(selectedYear - 1)}>
            <i className="fas fa-chevron-left" aria-hidden="true" />
          </button>
          <span className="rp2-year">{selectedYear}</span>
          <button type="button" className="rp2-year-arrow-btn" disabled={selectedYear >= currentYear}
            onClick={() => setSelectedYear((y) => Math.min(currentYear, y + 1))} aria-label={String(selectedYear + 1)}>
            <i className="fas fa-chevron-right" aria-hidden="true" />
          </button>
        </div>

        {/* Contenido por pestaña — canvas del demo (`.rp2-canvas`) con post-its `.postit-*` de F4.
            Anti-Spoiler por campaña (2026-09-23) — el servidor quita de los muros de recibidos lo
            protegido; `PostItWall` avisa en el muro propio. */}
        {tab === 'received' && (
          <PostItWall
            canvas
            profilePostIts
            year={selectedYear}
            emptyNode={emptyNode}
            emptyText={t('profile.empty')}
            baseParams={{ receptorId: targetId, programa: 'Fundamentales' }}
          />
        )}
        {tab === 'sent' && !isColleague && (
          // Server-side por emisorId propio (nunca con id de un tercero — REQ-120).
          <PostItWall canvas profilePostIts year={selectedYear} emptyNode={emptyNode} emptyText={t('prof.emptySent')} baseParams={{ emisorId: myId }} />
        )}
        {tab === 'brillamos' && brillamosHistoricoVisible && (
          <PostItWall
            canvas
            profilePostIts
            year={selectedYear}
            emptyNode={emptyNode}
            emptyText={t('prof.emptyBrillamos')}
            baseParams={{ receptorId: targetId, programa: 'Brillamos' }}
          />
        )}

        {/* Carpetas debajo del muro, en el mismo orden visual que el demo. Se oculta la sección
            completa cuando no hay ningún reconocimiento que mostrar (ni fundamentales ni Brillamos). */}
        {showFoldersSection && (
        <section className="rp2-folders-section">
          <h3 className="rp2-folders-title">📁 {t('prof.myFolders')}</h3>
          <div className="rp2-folders-row">
            {visibleFundFolders.map(({ fund, index, count }) => {
              const open = openFolder === fund.id;
              const folderStyle = { '--folder-color': getFundamentalColor(fund, index) } as React.CSSProperties;
              const fundName = getFundamentalName(fund, lang);
              return (
                <button
                  key={fund.id} type="button" style={folderStyle}
                  className={`rp2-folder ${open ? 'rp2-folder--open' : ''}`}
                  title={fundName} aria-label={fundName}
                  onClick={() => setOpenFolder(open ? null : fund.id)}
                >
                  <span className="rp2-folder-tab" />
                  <span className="rp2-folder-body">
                    <img src={getFundamentalIcon(fund, index)} alt="" className="rp2-folder-icon" />
                    <span className="rp2-folder-name">{fundName}</span>
                    <span className="rp2-folder-count">{count}</span>
                  </span>
                </button>
              );
            })}
            {brillamosFolderVisible && (
              <button
                type="button" className={`rp2-folder rp2-folder--shine ${openFolder === BRILLAMOS_FOLDER_ID ? 'rp2-folder--open' : ''}`}
                title={t('prof.brillamosFolder')} aria-label={t('prof.brillamosFolder')}
                onClick={() => { setOpenFolder(openFolder === BRILLAMOS_FOLDER_ID ? null : BRILLAMOS_FOLDER_ID); }}
              >
                <span className="rp2-folder-tab" />
                <span className="rp2-folder-body">
                  {/* QA (2026-09-01) — DT-029: mismo defecto que el banner/insignia de arriba, ruta
                      fija en vez de `shineLogoSrc(lang)`. Este era uno de los "usos restantes"
                      originales de DT-029 (27-ago) que se había quedado sin migrar por error de
                      conteo al cerrar el resto el 31-ago. */}
                  <img src={shineLogoSrc(lang, 'color')} alt="" className="rp2-folder-icon" />
                  <span className="rp2-folder-name">{t('prof.brillamosFolder')}</span>
                  <span className="rp2-folder-count">{brillamosCount}</span>
                </span>
              </button>
            )}
          </div>
        </section>
        )}

        <AnimatePresence>
          {openFolderView && verHistorico && (
            <ProfileFolderPanel
              key={openFolderView.id}
              title={openFund ? getFundamentalName(openFund, lang) : t('prof.brillamosFolder')}
              items={openFund ? recibidosFundamentales.filter((item) => item.fundamentalIds.includes(openFund.id)) : brillamosItems}
              catalog={funds}
              lang={lang}
              emptyText={t('prof.folderEmpty')}
              onClose={() => setOpenFolder(null)}
            />
          )}
        </AnimatePresence>

        {/* REQ-070 — Preferencias self-service de notificaciones (solo perfil propio). */}
        {!isColleague && <NotificationPreferences antiSpoilerActivo={antiSpoilerPropio} />}
      </div>

      {/* El overlay sólo se abre desde el botón o el aviso, y ninguno de los dos existe sin
          disponibilidad; el `wrappedAvailable` de aquí es la defensa en profundidad de que montarlo
          no dispare las consultas del Recap por una ruta que hoy no existe. La disponibilidad ya
          resuelta viaja como prop para que el overlay no la razone otra vez. */}
      {showWrapped && !isColleague && wrappedAvailable && verResumenMensual && (
        <WrappedModal
          catalog={funds}
          lang={lang}
          streakRecord={streak?.record}
          streakPeriodicidad={streak?.periodicidad}
          availability={availability.data}
          onClose={() => setShowWrapped(false)}
        />
      )}
      {showWrappedTeaser && (
        <div className="wrp-popup-overlay" role="dialog" aria-modal="true" onClick={dismissWrappedTeaser}>
          <div className="wrp-popup-card" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="wrp-popup-close" onClick={dismissWrappedTeaser} aria-label={t('common.close')}><i className="fas fa-xmark" /></button>
            <span className="wrp-popup-bgstar" aria-hidden="true">✦</span>
            <span className="wrp-popup-icon" aria-hidden="true">✨</span>
            <h2 className="wrp-popup-title">{t('wrapped.readyTitle')}</h2>
            <p className="wrp-popup-sub">{t('wrapped.readySubtitle', { year: wrappedYear })}</p>
            <button type="button" className="wrp-popup-cta" onClick={() => { dismissWrappedTeaser(); setShowWrapped(true); }}>{t('wrapped.open')}</button>
          </div>
        </div>
      )}

      {/* createPortal a document.body: `.plataforma-main` (recognition-shell.css) lleva `isolation:
          isolate`, que aísla el z-index de `.modal-overlay` dentro de su propio contexto de
          apilamiento — nunca compite contra el z-index explícito del Sidebar, que queda pintado
          encima. Mismo fix ya aplicado en BulkUploadModal.tsx/WinnersImportWizardModal.tsx. */}
      {showReconocerModal && isColleague && createPortal((
        <div className="modal-overlay modal-overlay--fade" role="dialog" aria-modal="true" onClick={() => setShowReconocerModal(false)}>
          <div className="rp2-reconocer-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="rec-modal-close" onClick={() => setShowReconocerModal(false)} aria-label={t('common.close')}>
              <i className="fas fa-times" aria-hidden="true" />
            </button>
            {/* 🔴 Reportado 7-sep-2026 — con campaña Brillamos en curso este modal era la última
                puerta abierta al reconocimiento DIARIO: montaba el panel sin `campaignId` ni
                `isBrillamosScreen`, así que el envío salía con `origen: 'Diario'` aunque el programa
                del usuario fuera Brillamos. El filtro del riel no lo tapaba porque «Mi perfil» está
                FUERA del grupo excluyente del menú (no es una campaña, se rige solo por rol).

                No se oculta el botón: se atribuye el envío a la campaña que rige para QUIEN ENVÍA.
                Sin campaña Brillamos vigente, `null`/`false` dejan el comportamiento de siempre
                (diario), que es lo correcto cuando el diario es el programa en curso. */}
            <ReconocerPanel
              initialRecipients={[viewed]}
              campaignId={brillamosDeQuienMira?.id ?? null}
              isBrillamosScreen={Boolean(brillamosDeQuienMira)}
              onSent={() => { setShowReconocerModal(false); setShowSparkle(true); }}
            />
          </div>
        </div>
      ), document.body)}

      {showSparkle && <SparkleOverlay onDone={() => setShowSparkle(false)} />}
    </div>
  );
}

export function PerfilView() {
  return (
    <RecognitionShell wide>
      <RoleGuard allow={['Employee', 'Manager', 'AdminOU', 'AdminPulso', 'AdminOUBrillamos', 'AdminBrillamos', 'AdminGlobal']}>
        <PerfilContent />
      </RoleGuard>
    </RecognitionShell>
  );
}

export default PerfilView;
